/**
 * AI 分析请求编排层：负责把业务参数组装成后端 SSE 接口所需的输入结构，
 * 并提供“运行分析”与“取消分析”两个原子操作。
 */
import type { IUseStreamingConversationResult } from '@dbl/core/global';
import { datalabWithTokenHttp } from '@/pages/datalab/services/common';
import type { IAiAnalysisStreamingInput, IAiPromptInfo } from './types';

export interface IBuildAiAnalysisInputParams {
  promptInfo?: IAiPromptInfo;
  requestPromptId?: string;
  requestPromptContent?: string;
  requestAnalysisContext?: string;
  promptId?: number;
  analysisContext?: string;
  promptContent?: string;
  taskIds?: string[];
  language?: string;
  session_id?: string;
  message_id?: string;
  refresh?: boolean;
}

interface IRunAiAnalysisParams extends IBuildAiAnalysisInputParams {
  session: IUseStreamingConversationResult<IAiAnalysisStreamingInput>;
}

/**
 * 将业务层参数转换为与 `/api/v2/datalab/ai/chat` 对齐的 IAiAnalysisStreamingInput。
 * - 其它字段仍按「有值才写入」；
 * - chat 固定传 analysis_context、prompt_content（在原有逻辑上扩展，不再下发 prompt_id）。
 */
export const buildAiAnalysisStreamingInput = (
  params: IBuildAiAnalysisInputParams,
): IAiAnalysisStreamingInput => {
  const {
    analysisContext,
    promptContent,
    taskIds,
    language,
    session_id: sessionId,
    message_id: messageId,
    refresh,
  } = params;

  const input: IAiAnalysisStreamingInput = {};
  // 后端新契约：以正文为准，prompt_id 暂不在请求体中下发（params.promptId 保留便于回滚）
  // if (params.promptId) {
  //   input.prompt_id = params.promptId;
  // }
  input.analysis_context = String(analysisContext ?? '');
  input.prompt_content = String(promptContent ?? '');
  if (taskIds) {
    input.task_ids = taskIds;
  }
  if (language) {
    input.language = language;
  }
  if (sessionId) {
    input.session_id = sessionId;
  }
  if (messageId) {
    input.message_id = messageId;
  }
  if (refresh) {
    input.refresh = refresh;
  }

  return input;
};

/**
 * 触发一次 AI 分析：先清空当前会话历史，再发送流式请求。
 * 注意：clearConversations 必须在 sendMessage 之前执行，否则新回答会追加到旧对话中。
 */
export const runAiAnalysis = async (params: IRunAiAnalysisParams): Promise<void> => {
  const { session } = params;
  const input = buildAiAnalysisStreamingInput(params);
  session.clearConversations();
  await session.sendMessage({ input });
};

export interface ICancelAiAnalysisParams {
  sessionId?: string;
  messageId?: string;
}

/**
 * 取消正在进行的 AI 分析。
 * - 若 sessionId 或 messageId 缺失则静默返回，避免无效请求。
 * - 后端依赖这两个字段定位到具体的 SSE 连接并中断生成。
 */
export const cancelAiAnalysis = async (params: ICancelAiAnalysisParams): Promise<void> => {
  const { sessionId, messageId } = params;
  if (!sessionId || !messageId) {
    return;
  }
  await datalabWithTokenHttp({
    url: '/api/v2/datalab/ai/chat_cancel',
    method: 'post',
    data: {
      session_id: sessionId,
      message_id: messageId,
    },
    useCustomTips: true,
  });
};
